import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { trackEvent } from '../../Analytics/TrackedClick';
import { useStatsig } from '../../hooks/useStatsig';
import { performanceStatsAnalyticsEvents, getActivityStatsTraits } from '../../../utils/analytics';
import { PlayerProfileTabIdentifiers } from '../../../utils/constants';
import styles from './performanceStatsDiscoveryBanner.scss';

const PERFORMANCE_STATS_VIEW = 'performance';

const PerformanceStatsDiscoveryBanner = ({ 'data-testid': dataTestId }) => {
    const { checkGate, featureGates } = useStatsig();
    const auth = useSelector((state) => state.auth);
    const ownPlayer = useSelector((state) => state.profile?.data);
    const playerId = auth?.user?.claimedPlayer?.playerId;
    // DE-2777 Phase 1: Performance Stats is hidden for Pro players, so the banner
    // must not promote a stats tab that would be empty for them. DashboardContentV2
    // loads the viewer's own profile into the profile slice on mount; the id check
    // keeps us from reading a previously-viewed player's data, and means the banner
    // waits for that fetch rather than briefly showing to a Pro. Evaluated before
    // checkGate so excluded users log no Statsig exposure. Phase 3 drops the isPro check.
    const isOwnPlayerLoaded = !!playerId && ownPlayer?.id === playerId;
    const isVisible = isOwnPlayerLoaded &&
        !ownPlayer?.isPro &&
        checkGate(featureGates.PERFORMANCE_STATS_DISCOVERY_BANNER) &&
        checkGate(featureGates.POWER_PERFORMANCE_STATS);
    const hasTrackedImpression = useRef(false);

    useEffect(() => {
        if (isVisible && !hasTrackedImpression.current) {
            hasTrackedImpression.current = true;
            trackEvent(
                performanceStatsAnalyticsEvents.DISCOVERY_BANNER_VIEWED,
                getActivityStatsTraits(auth, playerId, true),
            );
        }
    // auth changes identity on updates unrelated to the banner, so this effect re-runs
    // several times per visit. The ref holds the impression to one per mount.
    }, [auth, isVisible, playerId]);

    if (!isVisible) {
        return null;
    }

    const handleCtaClick = () => {
        trackEvent(
            performanceStatsAnalyticsEvents.DISCOVERY_BANNER_TAPPED,
            getActivityStatsTraits(auth, playerId, true),
        );
    };

    return (
        <div className={styles.banner} data-testid={dataTestId && `${dataTestId}.container`}>
            <span className={styles.text} data-testid={dataTestId && `${dataTestId}.text`}>
                <span className={styles.headline} data-testid={dataTestId && `${dataTestId}.headline`}>
                    New Performance Stats!
                </span>
                <span className={styles.subhead} data-testid={dataTestId && `${dataTestId}.subhead`}>
                    See how you performed in each match compared to your UTR Rating.
                </span>
            </span>
            <Link
                className={styles.cta}
                to={`/profiles/${playerId}?t=${PlayerProfileTabIdentifiers.STATS}&view=${PERFORMANCE_STATS_VIEW}`}
                onClick={handleCtaClick}
                data-testid={dataTestId && `${dataTestId}.cta`}
            >
                View My Stats
            </Link>
        </div>
    );
};

PerformanceStatsDiscoveryBanner.propTypes = {
    'data-testid': PropTypes.string,
};

PerformanceStatsDiscoveryBanner.defaultProps = {
    'data-testid': null,
};

export default PerformanceStatsDiscoveryBanner;
